import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Biotech as BiotechIcon,
  WaterDrop as WaterDropIcon,
  Verified as VerifiedIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import qcInternalService, {
  InstrumentQcCategory,
  InstrumentQcPoint,
  InstrumentQcRun,
} from '../../services/qcInternalService';

const LAB_OPTIONS = [
  { value: '', label: 'Tous laboratoires' },
  { value: 'LABORATOIRE_ICP', label: 'ICP' },
  { value: 'LABORATOIRE_SAA', label: 'SAA' },
  { value: 'LABORATOIRE_AU', label: 'Au (Fire Assay)' },
];

const CATEGORY_LABEL: Record<InstrumentQcCategory, string> = {
  blank: 'Blanc instrument (BLK)',
  standard: 'Étalonnage (STD)',
  solution_blank: 'Blanc solution (BL)',
  crm: 'OREAS / MRC',
  duplicate: 'Duplicata',
};

const CATEGORY_COLOR: Record<
  InstrumentQcCategory,
  'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'
> = {
  blank: 'info',
  standard: 'primary',
  solution_blank: 'info',
  crm: 'success',
  duplicate: 'warning',
};

/**
 * QC Interne tab — admin / analyst view of instrument-level QC.
 *
 * Two-pane workflow:
 *   1. Run list (left)  — every imported CSV with counters and status icons
 *   2. Run detail (full screen on click) — split into:
 *        a. Overview     : header card + run summary
 *        b. Étalonnage   : one row per (label, element) for STD rows + target ppm
 *        c. Blancs       : BLK / BL solution blanks, value vs detection threshold
 *        d. OREAS / MRC  : measured vs certified value + % recovery
 *        e. Run sheet    : every QC row in CSV order (forensic view)
 *
 * Designed to never render client / R-… sample readings — those have their
 * own validation and certificate flow.
 */
const QcInterneTab: React.FC = () => {
  const [runs, setRuns] = useState<InstrumentQcRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [laboratory, setLaboratory] = useState<string>('LABORATOIRE_ICP');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<InstrumentQcRun | null>(null);
  const [points, setPoints] = useState<InstrumentQcPoint[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailTab, setDetailTab] = useState<number>(0);

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    setError(null);
    try {
      const res = await qcInternalService.listRuns({
        laboratory: laboratory || undefined,
        from: from || undefined,
        to: to || undefined,
        limit: 50,
      });
      setRuns(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.message || "Erreur de chargement des runs d'instrument");
    } finally {
      setLoadingRuns(false);
    }
  }, [laboratory, from, to]);

  const loadRunDetail = useCallback(async (id: string) => {
    setLoadingDetail(true);
    setError(null);
    try {
      const res = await qcInternalService.getRun(id);
      setSelectedRun(res.run);
      setPoints(res.points);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement du run');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (selectedRunId) loadRunDetail(selectedRunId);
    else {
      setSelectedRun(null);
      setPoints([]);
    }
  }, [selectedRunId, loadRunDetail]);

  // ===========================================================================
  //  RUN LIST
  // ===========================================================================
  if (!selectedRunId) {
    return (
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Laboratoire</InputLabel>
                <Select
                  label="Laboratoire"
                  value={laboratory}
                  onChange={(e) => setLaboratory(e.target.value as string)}
                >
                  {LAB_OPTIONS.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Du"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Au"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Rafraîchir">
                  <IconButton size="small" onClick={loadRuns} disabled={loadingRuns}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" color="text.secondary">
                  {total} run{total > 1 ? 's' : ''} d'instrument importé
                  {total > 1 ? 's' : ''}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mb: 2 }}>
          QC Interne capture chaque fichier CSV importé depuis l'instrument
          (ICP / SAA / Au) — tubes BLK, étalons (STD), blancs solution (BL) et
          MRC OREAS — pour audit interne. Cette section n'est jamais visible
          côté client.
        </Alert>

        {loadingRuns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : runs.length === 0 ? (
          <Alert severity="warning">
            Aucun run d'instrument trouvé. Importez un fichier CSV depuis le
            superviseur ICP pour démarrer la capture.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {runs.map((run) => (
              <Grid item xs={12} md={6} lg={4} key={run.id}>
                <RunSummaryCard
                  run={run}
                  onOpen={() => setSelectedRunId(run.id)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  }

  // ===========================================================================
  //  RUN DETAIL
  // ===========================================================================
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => setSelectedRunId(null)}
          variant="text"
        >
          Retour à la liste
        </Button>
        {selectedRun && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={selectedRun.laboratory.replace('LABORATOIRE_', '')}
              color="primary"
              variant="outlined"
            />
            {selectedRun.analysis_type && (
              <Chip
                size="small"
                label={selectedRun.analysis_type}
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Stack>

      {loadingDetail || !selectedRun ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <RunHeaderCard run={selectedRun} />

          <Paper sx={{ mt: 2 }}>
            <Tabs
              value={detailTab}
              onChange={(_, v) => setDetailTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<ShowChartIcon />} iconPosition="start" label="Vue d'ensemble" />
              <Tab icon={<ScienceIcon />} iconPosition="start" label="Étalonnage (STD)" />
              <Tab icon={<WaterDropIcon />} iconPosition="start" label="Blancs (BLK / BL)" />
              <Tab icon={<VerifiedIcon />} iconPosition="start" label="OREAS / MRC" />
              <Tab icon={<BiotechIcon />} iconPosition="start" label="Run sheet" />
            </Tabs>
            <Divider />
            <Box sx={{ p: 2 }}>
              {detailTab === 0 && <RunOverview run={selectedRun} points={points} />}
              {detailTab === 1 && <StandardsTable points={points} />}
              {detailTab === 2 && <BlanksTable points={points} />}
              {detailTab === 3 && <OreasRecoveryTable points={points} />}
              {detailTab === 4 && <RunSheetTable points={points} />}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

// =============================================================================
//  RUN LIST CARD
// =============================================================================
const RunSummaryCard: React.FC<{
  run: InstrumentQcRun;
  onOpen: () => void;
}> = ({ run, onOpen }) => (
  <Card
    variant="outlined"
    sx={{
      cursor: 'pointer',
      transition: 'all 120ms',
      '&:hover': { boxShadow: 3, borderColor: 'primary.main' },
    }}
    onClick={onOpen}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
            {run.filename}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {run.run_date
              ? new Date(run.run_date).toLocaleString()
              : new Date(run.imported_at).toLocaleString()}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={run.laboratory.replace('LABORATOIRE_', '')}
          color="primary"
          variant="outlined"
        />
      </Stack>

      <Grid container spacing={1} sx={{ mt: 1 }}>
        <Grid item xs={3}>
          <Typography variant="caption" color="text.secondary">
            Blancs
          </Typography>
          <Typography variant="h6">{run.total_blanks}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" color="text.secondary">
            Étalons
          </Typography>
          <Typography variant="h6">{run.total_standards}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" color="text.secondary">
            CRM
          </Typography>
          <Typography variant="h6">{run.total_crms}</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography variant="caption" color="text.secondary">
            Échant.
          </Typography>
          <Typography variant="h6">{run.total_samples}</Typography>
        </Grid>
      </Grid>

      {run.lots.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
          {run.lots.slice(0, 4).map((lot) => (
            <Chip key={lot} size="small" label={lot} variant="outlined" />
          ))}
          {run.lots.length > 4 && (
            <Chip size="small" label={`+${run.lots.length - 4}`} variant="outlined" />
          )}
        </Stack>
      )}
    </CardContent>
  </Card>
);

// =============================================================================
//  RUN HEADER (detail mode)
// =============================================================================
const RunHeaderCard: React.FC<{ run: InstrumentQcRun }> = ({ run }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
        {run.filename}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Exécuté{' '}
        {run.run_date
          ? new Date(run.run_date).toLocaleString()
          : '— date instrument inconnue —'}
        {' · '}importé le {new Date(run.imported_at).toLocaleString()}
      </Typography>

      <Grid container spacing={2}>
        <KpiCell label="Blancs (BLK / BL)" value={run.total_blanks} />
        <KpiCell label="Étalons (STD)" value={run.total_standards} />
        <KpiCell label="CRM (OREAS)" value={run.total_crms} />
        <KpiCell label="Échantillons" value={run.total_samples} />
        <KpiCell
          label="Lots détectés"
          value={run.lots.length}
          tooltip={run.lots.join(', ') || '—'}
        />
      </Grid>
    </CardContent>
  </Card>
);

const KpiCell: React.FC<{ label: string; value: number; tooltip?: string }> = ({
  label,
  value,
  tooltip,
}) => {
  const inner = (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5">{value}</Typography>
    </Box>
  );
  return (
    <Grid item xs={6} md={2}>
      {tooltip ? <Tooltip title={tooltip}>{inner}</Tooltip> : inner}
    </Grid>
  );
};

// =============================================================================
//  OVERVIEW
// =============================================================================
const RunOverview: React.FC<{
  run: InstrumentQcRun;
  points: InstrumentQcPoint[];
}> = ({ points }) => {
  const byCategory = useMemo(() => {
    const acc: Record<InstrumentQcCategory, number> = {
      blank: 0,
      standard: 0,
      solution_blank: 0,
      crm: 0,
      duplicate: 0,
    };
    points.forEach((p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
    });
    return acc;
  }, [points]);

  const oreasRecoveryStats = useMemo(() => {
    const crmRows = points.filter(
      (p) => p.category === 'crm' && p.recovery_percent != null,
    );
    if (crmRows.length === 0)
      return { count: 0, mean: null as number | null, outliers: 0 };
    const recoveries = crmRows.map((r) => r.recovery_percent as number);
    const mean = recoveries.reduce((a, b) => a + b, 0) / recoveries.length;
    const outliers = recoveries.filter((r) => r < 90 || r > 110).length;
    return { count: crmRows.length, mean, outliers };
  }, [points]);

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Répartition par catégorie ({points.length} mesures QC)
      </Typography>
      <Grid container spacing={2}>
        {(Object.keys(byCategory) as InstrumentQcCategory[]).map((cat) => (
          <Grid item xs={6} md={3} key={cat}>
            <Card variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={CATEGORY_LABEL[cat]}
                    color={CATEGORY_COLOR[cat]}
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="h4" sx={{ mt: 1 }}>
                  {byCategory[cat]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {oreasRecoveryStats.count > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Récupération MRC (vs valeurs OREAS certifiées)
          </Typography>
          <Stack direction="row" spacing={2}>
            <Card variant="outlined" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  Mesures avec MRC matché
                </Typography>
                <Typography variant="h5">{oreasRecoveryStats.count}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  Récupération moyenne
                </Typography>
                <Typography variant="h5">
                  {oreasRecoveryStats.mean?.toFixed(1)} %
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">
                  Hors plage 90–110 %
                </Typography>
                <Typography
                  variant="h5"
                  color={oreasRecoveryStats.outliers > 0 ? 'error' : 'success.main'}
                >
                  {oreasRecoveryStats.outliers}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
//  STANDARDS TABLE
// =============================================================================
const StandardsTable: React.FC<{ points: InstrumentQcPoint[] }> = ({ points }) => {
  const stdRows = points.filter((p) => p.category === 'standard');
  if (stdRows.length === 0) return <EmptyState text="Aucun étalon dans ce run." />;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Position</TableCell>
            <TableCell>Étalon</TableCell>
            <TableCell>Élément</TableCell>
            <TableCell align="right">Valeur cible</TableCell>
            <TableCell align="right">Valeur mesurée</TableCell>
            <TableCell>Unité</TableCell>
            <TableCell>Mesuré le</TableCell>
            <TableCell>Flags</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {stdRows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>{p.position_in_run}</TableCell>
              <TableCell>{p.label}</TableCell>
              <TableCell>{p.element}</TableCell>
              <TableCell align="right">
                {p.expected_value != null ? p.expected_value : '—'}
              </TableCell>
              <TableCell align="right">
                {p.concentration != null ? p.concentration : '—'}
              </TableCell>
              <TableCell>{p.unit || '—'}</TableCell>
              <TableCell>
                {p.measured_at ? new Date(p.measured_at).toLocaleTimeString() : '—'}
              </TableCell>
              <TableCell>
                {p.flags ? <Chip size="small" label={p.flags} color="warning" /> : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// =============================================================================
//  BLANKS TABLE
// =============================================================================
const BlanksTable: React.FC<{ points: InstrumentQcPoint[] }> = ({ points }) => {
  const blankRows = points.filter(
    (p) => p.category === 'blank' || p.category === 'solution_blank',
  );
  if (blankRows.length === 0) return <EmptyState text="Aucun blanc dans ce run." />;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Position</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Élément</TableCell>
            <TableCell align="right">Concentration</TableCell>
            <TableCell>Unité</TableCell>
            <TableCell align="right">ppm normalisé</TableCell>
            <TableCell>Mesuré le</TableCell>
            <TableCell>Flags</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {blankRows.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>{p.position_in_run}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={p.category === 'blank' ? 'BLK instrument' : 'BL solution'}
                  color={p.category === 'blank' ? 'info' : 'default'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{p.label}</TableCell>
              <TableCell>{p.element}</TableCell>
              <TableCell align="right">
                {p.concentration != null ? p.concentration.toFixed(4) : '—'}
              </TableCell>
              <TableCell>{p.unit || '—'}</TableCell>
              <TableCell align="right">
                {p.concentration_ppm != null ? p.concentration_ppm.toFixed(4) : '—'}
              </TableCell>
              <TableCell>
                {p.measured_at ? new Date(p.measured_at).toLocaleTimeString() : '—'}
              </TableCell>
              <TableCell>
                {p.flags ? <Chip size="small" label={p.flags} color="warning" /> : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// =============================================================================
//  OREAS RECOVERY TABLE
// =============================================================================
const OreasRecoveryTable: React.FC<{ points: InstrumentQcPoint[] }> = ({ points }) => {
  const crmRows = points.filter((p) => p.category === 'crm');
  if (crmRows.length === 0) return <EmptyState text="Aucun MRC dans ce run." />;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Position</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Élément</TableCell>
            <TableCell align="right">Valeur certifiée</TableCell>
            <TableCell align="right">Mesurée (ppm)</TableCell>
            <TableCell align="right">% Récupération</TableCell>
            <TableCell>Statut</TableCell>
            <TableCell>Mesuré le</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {crmRows.map((p) => {
            const recovery = p.recovery_percent;
            let status: 'OK' | 'WARN' | 'OUT' | 'NA' = 'NA';
            if (recovery != null) {
              if (recovery >= 95 && recovery <= 105) status = 'OK';
              else if (recovery >= 90 && recovery <= 110) status = 'WARN';
              else status = 'OUT';
            }
            const statusColor: Record<typeof status, 'success' | 'warning' | 'error' | 'default'> = {
              OK: 'success',
              WARN: 'warning',
              OUT: 'error',
              NA: 'default',
            };
            const statusLabel: Record<typeof status, string> = {
              OK: 'Conforme (95–105%)',
              WARN: 'Alerte (90–110%)',
              OUT: 'Hors plage',
              NA: 'MRC non catalogué',
            };
            return (
              <TableRow key={p.id} hover>
                <TableCell>{p.position_in_run}</TableCell>
                <TableCell>{p.label}</TableCell>
                <TableCell>{p.element}</TableCell>
                <TableCell align="right">
                  {p.expected_value != null
                    ? `${p.expected_value} ${p.expected_unit || ''}`
                    : '— non catalogué —'}
                </TableCell>
                <TableCell align="right">
                  {p.concentration_ppm != null ? p.concentration_ppm.toFixed(3) : '—'}
                </TableCell>
                <TableCell align="right">
                  {recovery != null ? `${recovery.toFixed(1)} %` : '—'}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={statusLabel[status]} color={statusColor[status]} />
                </TableCell>
                <TableCell>
                  {p.measured_at ? new Date(p.measured_at).toLocaleTimeString() : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// =============================================================================
//  RUN SHEET (every QC row, in CSV order)
// =============================================================================
const RunSheetTable: React.FC<{ points: InstrumentQcPoint[] }> = ({ points }) => {
  if (points.length === 0)
    return <EmptyState text="Aucune ligne QC dans ce run." />;
  return (
    <TableContainer sx={{ maxHeight: 600 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Catégorie</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Type CSV</TableCell>
            <TableCell>Élément</TableCell>
            <TableCell>λ</TableCell>
            <TableCell align="right">Valeur</TableCell>
            <TableCell>Unité</TableCell>
            <TableCell align="right">ppm</TableCell>
            <TableCell>Heure</TableCell>
            <TableCell>Flags</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {points.map((p) => (
            <TableRow key={p.id} hover>
              <TableCell>{p.position_in_run}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={CATEGORY_LABEL[p.category]}
                  color={CATEGORY_COLOR[p.category]}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>{p.label}</TableCell>
              <TableCell>{p.csv_type || '—'}</TableCell>
              <TableCell>{p.element}</TableCell>
              <TableCell>{p.wavelength || '—'}</TableCell>
              <TableCell align="right">
                {p.concentration != null ? p.concentration : '—'}
              </TableCell>
              <TableCell>{p.unit || '—'}</TableCell>
              <TableCell align="right">
                {p.concentration_ppm != null ? p.concentration_ppm.toFixed(4) : '—'}
              </TableCell>
              <TableCell>
                {p.measured_at ? new Date(p.measured_at).toLocaleTimeString() : '—'}
              </TableCell>
              <TableCell>
                {p.flags ? <Chip size="small" label={p.flags} color="warning" /> : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <Alert severity="info">{text}</Alert>
);

export default QcInterneTab;
